import React, { useMemo } from "react";
import "./associations-block.css";

// icon can be:
// - ReactNode (preferred)
// - { type: 'img', src: '...' } for url/base64
// - { type: 'svg', src: '<svg ...>' } raw svg string
const AssocIcon = ({ icon }) => {
  if (!icon) return null;

  if (typeof icon === "string" && icon.startsWith("data:image/svg+xml;base64,")) {
    const b64 = icon.split(",")[1];
    let svg = atob(b64);

    svg = svg
      .replace(/stroke="[^"]*"/g, 'stroke="currentColor"')
      .replace(/fill="[^"]*"/g, 'fill="currentColor"');

    return (
      <span
        className="assoc-chip__icon assoc-chip__icon--svg"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  // звичайні url/png і т.д.
  if (typeof icon === "string") {
    return <img className="assoc-chip__icon assoc-chip__icon--img" src={icon} alt="" />;
  }

  if (icon?.type === "svg" && icon?.src) {
    return (
      <span
        className="assoc-chip__icon assoc-chip__icon--svg"
        dangerouslySetInnerHTML={{ __html: icon.src }}
      />
    );
  }

  return null;
};

const AssocChip = ({ item }) => {
  const clickable = !!item?.href || typeof item?.onClick === "function";
  const Tag = item?.href ? "a" : "button";

  const commonProps = item?.href
    ? {
        href: item.href,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : { type: "button", onClick: item?.onClick };

  return (
    <Tag
      {...commonProps}
      className={`assoc-chip ${clickable ? "assoc-chip--clickable" : ""}`}
      onClick={(e) => {
        if (!item?.onClick) return;
        e.preventDefault();
        e.stopPropagation();
        item.onClick(e);
      }}
      title={item?.label}
    >
      <AssocIcon icon={item?.icon ?? item?.iconBase64} />
      <span className="assoc-chip__label">{item?.label}</span>
    </Tag>
  );
};

export default function AssociationsBlock({
  title = "Associations",
  groups = [],
  className = "",
  emptyText = "No associations",
}) {
  const normalized = useMemo(() => {
    return (groups || [])
      .filter(Boolean)
      .map((g) => ({
        key: g.key || g.title,
        title: g.title,
        items: Array.isArray(g.items) ? g.items.filter(Boolean) : [],
        count: Number.isInteger(g.count) ? g.count : null,
      }))
      .filter((g) => g.title);
  }, [groups]);

  const total = useMemo(() => {
    return normalized.reduce((sum, g) => sum + (g.count ?? g.items.length), 0);
  }, [normalized]);

  return (
    <div className={`assoc ${className}`}>
      <div className="assoc__title">
        <span>{title}</span>
        <span className="assoc__title-count">{total}</span>
      </div>

      {normalized.length === 0 ? (
        <div className="assoc__empty">{emptyText}</div>
      ) : (
        <div className="assoc__groups">
          {normalized.map((g) => (
            <div key={g.key} className="assoc__group">
              <div className="assoc__group-title">
                <span>{g.title}</span>
                <span className="assoc__group-count">{g.count ?? g.items.length}</span>
              </div>

              <div className="assoc__chips">
                {g.items.map((it) => (
                  <AssocChip key={it.id || it.key || it.label} item={it} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}